import { BooleanDataType } from 'constants/data';
import { PersonStatusCodeType } from 'constants/person';
import {
  SUPPLIER_ID,
  SupplierVendorMappingCollection,
  SupplierVendorMappingSupplier,
  SupplierVendorMappingItem
} from 'constants/supplier';

//
// --- Types ---

// cSpell:disable

export interface RawSupplierGroupType {
  creationtime: number; // timestamp in milliseconds
  groupname: string;
  id: number;
  portalid: number;
  portalSuppliers: ReadonlyArray<RawSupplierGroupCompanyType>;
  updatedbyuserkey: number;
  updatedtime: number; // timestamp in milliseconds
}

export interface RawSupplierGroupCompanyType {
  companykey: number;
  companyname: string;
  creationtime: number; // timestamp in milliseconds
  /** primary key for supplier group company mapping record; this is not an eip company key or an asap supplier id */
  id: number;
  isdefault: BooleanDataType;
  portalid: number;
  suppliergroupid: number;
  supplierUsers: ReadonlyArray<RawSupplierGroupCompanyPersonType> | null;
}

export interface RawSupplierGroupCompanyPersonType {
  emailaddress: string;
  firstname: string;
  id: number;
  isdefault: string;
  lastloginsuccess: number; // a timestamp in milliseconds
  lastname: string;
  lastviewtime: number | null;
  personkey: number;
  personstatuscode: PersonStatusCodeType;
  phonenumber: string;
  supplierportalsupplierid: number;
  username: string;
}

export type SupplierGroupCompanyMap = Record<string, SupplierGroupCompanyType>;
export type SupplierGroupPersonMap = Record<number, SupplierGroupCompanyPersonType>;

export interface SupplierGroupType {
  id: number;
  name: string;
  portalId: number;
  supplierPortalSupplierCompanyKeys: ReadonlyArray<number>;
  supplierPortalSupplierUserPersonKeys: ReadonlyArray<number>;
  suppliers: SupplierGroupCompanyMap;
}

export interface SupplierGroupCompanyType {
  companyKey: number;
  /** this is a supplier id */
  id: string;
  name: string;
  people: SupplierGroupPersonMap;
  /** list of supplier group company mapping ids; this is not a company key or supplier id */
  supplierGroupCompanyIds: ReadonlyArray<number>;
}

export interface SupplierGroupCompanyPersonType {
  companyName: string;
  email: string;
  firstName: string;
  id: number;
  isMainContact: boolean;
  lastLoginSuccessTimestamp: number; // a timestamp in milliseconds
  lastName: string;
  lastViewTime: number | null;
  name: string;
  personKey: number;
  personStatusCode: PersonStatusCodeType;
  supplierId: string;
  workPhone: string;
  username: string;
}

type TransformCompanyKeyToSupplierMap = Record<number, SupplierVendorMappingSupplier | null>;

// cSpell:enable

//
// --- Functions ---

export function transformToCuratedSupplierGroups(
  rawSupplierGroups: ReadonlyArray<RawSupplierGroupType>,
  supplierVendorMappings: SupplierVendorMappingCollection = []
): ReadonlyArray<SupplierGroupType> {
  // NOTE: if supplier vendor mappings missing, results would make all companies look like EIP companies, so return empty array instead
  if (
    !Array.isArray(rawSupplierGroups) ||
    !rawSupplierGroups.length ||
    !Array(supplierVendorMappings) ||
    !supplierVendorMappings.length
  ) {
    return [];
  }

  const eipCompanyKeys = getCompanyKeysFromRawSupplierGroups(rawSupplierGroups);
  const eipCompanyKeyToVendorSupplierMap = getSupplierMapForCompanyKeys(supplierVendorMappings, eipCompanyKeys);

  return rawSupplierGroups.map((rawSupplierGroup: RawSupplierGroupType) =>
    transformRawSupplierGroup(rawSupplierGroup, eipCompanyKeyToVendorSupplierMap)
  );
}

export function transformRawSupplierGroup(
  rawSupplierGroup: RawSupplierGroupType,
  companyKeyToSupplierMap: TransformCompanyKeyToSupplierMap
): SupplierGroupType {
  // cSpell:disable
  const { id, groupname: name, portalid: portalId, portalSuppliers } = rawSupplierGroup || {};
  // cSpell:enable

  const hasPortalSuppliers = Array.isArray(portalSuppliers) && portalSuppliers.length > 0;
  const hasValidCompanyKeyToSupplierMap =
    companyKeyToSupplierMap && typeof companyKeyToSupplierMap === 'object' && !Array.isArray(companyKeyToSupplierMap);

  // create company supplier map for this supplier group
  const supplierGroupCompanyMap: SupplierGroupCompanyMap =
    hasPortalSuppliers && hasValidCompanyKeyToSupplierMap
      ? portalSuppliers.reduce<SupplierGroupCompanyMap>(
          (companyMap, currentRawSupplierCompany: RawSupplierGroupCompanyType) => {
            const vendorSupplier = companyKeyToSupplierMap[currentRawSupplierCompany.companykey];

            // skip if company is **undefined** only in supplier map (otherwise all companies will appear like an EIP company)
            // IMPORTANT: null is a valid value in company map used to signify an EIP only company
            if (vendorSupplier === undefined) {
              return companyMap;
            }

            const supplierId =
              vendorSupplier === null
                ? `${SUPPLIER_ID.EIP_COMPANY_PREFIX}${currentRawSupplierCompany.companykey}`
                : vendorSupplier.id;
            const name = vendorSupplier === null ? currentRawSupplierCompany.companyname : vendorSupplier.name;
            const companyKey = currentRawSupplierCompany.companykey;
            const peopleFromCurrentCompany = Array.isArray(currentRawSupplierCompany.supplierUsers)
              ? transformRawSupplierGroupPeopleToPersonMap(
                  currentRawSupplierCompany.supplierUsers,
                  currentRawSupplierCompany.companyname,
                  supplierId
                )
              : [];

            // if supplier does not exist, add to map and add people
            if (!companyMap[supplierId]) {
              return {
                ...companyMap,
                [supplierId]: {
                  companyKey,
                  id: supplierId,
                  name,
                  people: { ...peopleFromCurrentCompany },
                  supplierGroupCompanyIds: currentRawSupplierCompany.id ? [currentRawSupplierCompany.id] : []
                }
              };
            }

            // if supplier does exist, merge people
            return {
              ...companyMap,
              [supplierId]: {
                ...companyMap[supplierId],
                people: { ...companyMap[supplierId].people, ...peopleFromCurrentCompany },
                supplierGroupCompanyIds: !companyMap[supplierId].supplierGroupCompanyIds.includes(
                  currentRawSupplierCompany.id
                )
                  ? [...companyMap[supplierId].supplierGroupCompanyIds, currentRawSupplierCompany.id]
                  : companyMap[supplierId].supplierGroupCompanyIds
              }
            };
          },
          {}
        )
      : {};

  return {
    id,
    name,
    portalId,
    /** list for checking whether a company is in the supplier group */
    supplierPortalSupplierCompanyKeys: hasPortalSuppliers
      ? rawSupplierGroup.portalSuppliers.map(rawCompany => rawCompany.companykey)
      : [],
    /** list for checking whether a person is in the supplier group */
    supplierPortalSupplierUserPersonKeys: hasPortalSuppliers
      ? rawSupplierGroup.portalSuppliers.reduce<ReadonlyArray<number>>(
          (personKeys, currentRawCompany) =>
            Array.isArray(currentRawCompany.supplierUsers) && currentRawCompany.supplierUsers.length > 0
              ? personKeys.concat(currentRawCompany.supplierUsers.map(rawPerson => rawPerson.personkey))
              : personKeys,
          []
        )
      : [],
    suppliers: supplierGroupCompanyMap
  };
}

export function transformRawSupplierGroupPeopleToPersonMap(
  people: ReadonlyArray<RawSupplierGroupCompanyPersonType>,
  companyName: string,
  supplierId: string
): SupplierGroupPersonMap {
  if (!Array.isArray(people) || !people.length) {
    return {};
  }

  return people.reduce<SupplierGroupPersonMap>(
    (personMap, currentRawPerson) => ({
      ...personMap,
      [currentRawPerson.personkey]: transformRawSupplierGroupCompanyPerson(currentRawPerson, companyName, supplierId)
    }),
    {}
  );
}

export function transformRawSupplierGroupCompanyPerson(
  rawCompanyPerson: RawSupplierGroupCompanyPersonType,
  companyName: string,
  supplierId: string
): SupplierGroupCompanyPersonType {
  // cSpell:disable
  const {
    emailaddress: email,
    firstname: firstName,
    id,
    isdefault: isDefault,
    lastloginsuccess: lastLoginSuccess,
    lastname: lastName,
    lastviewtime: lastViewTime,
    personkey: personKey,
    personstatuscode: personStatusCode,
    phonenumber: phoneNumber,
    username
  } = rawCompanyPerson || {};
  // cSpell:enable

  return {
    companyName,
    email,
    firstName,
    id,
    isMainContact: isDefault === 'Y',
    lastLoginSuccessTimestamp: lastLoginSuccess,
    lastName,
    lastViewTime,
    name: [firstName, lastName].filter(value => value).join(' '),
    personKey,
    personStatusCode,
    supplierId,
    workPhone: phoneNumber,
    username
  };
}

/**
 * Iterate through an array of raw supplier groups and return a list of all company keys for
 * supplier companies in the groups.
 */
export function getCompanyKeysFromRawSupplierGroups(
  rawSupplierGroups: ReadonlyArray<RawSupplierGroupType>
): ReadonlyArray<number> {
  if (!Array.isArray(rawSupplierGroups) || !rawSupplierGroups.length) {
    return [];
  }

  return rawSupplierGroups.reduce((companyKeys, currentSupplierGroup) => {
    return Array.isArray(currentSupplierGroup.portalSuppliers) && currentSupplierGroup.portalSuppliers.length
      ? companyKeys.concat(
          currentSupplierGroup.portalSuppliers.map(
            (rawPortalSupplier: RawSupplierGroupCompanyType) => rawPortalSupplier.companykey
          )
        )
      : companyKeys;
  }, []);
}

/**
 * Get supplier record from supplier vendor mappings for a given EIP company key
 */

export function getSupplierVendorMappingSupplierForEIPCompanyKey(
  supplierVendorMappings: SupplierVendorMappingCollection,
  eipCompanyKey: number
): SupplierVendorMappingSupplier | null {
  if (!Array.isArray(supplierVendorMappings) || !supplierVendorMappings.length) {
    return null;
  }

  const supplierMappingItem = supplierVendorMappings.find((mappingItem: SupplierVendorMappingItem) => {
    return mappingItem.vendors.find(vendor => vendor.eip_companykey === eipCompanyKey);
  });

  return supplierMappingItem && supplierMappingItem.supplier.id !== SUPPLIER_ID.UNFAMILIED
    ? supplierMappingItem.supplier
    : null;
}

export function getSupplierMapForCompanyKeys(
  supplierVendorMappings: SupplierVendorMappingCollection,
  companyKeys: ReadonlyArray<number>
): TransformCompanyKeyToSupplierMap {
  if (
    !Array.isArray(supplierVendorMappings) ||
    !supplierVendorMappings.length ||
    !Array.isArray(companyKeys) ||
    !companyKeys.length
  ) {
    return {};
  }

  return companyKeys.reduce<TransformCompanyKeyToSupplierMap>((supplierMap, currentCompanyKey) => {
    return {
      ...supplierMap,
      [currentCompanyKey]: getSupplierVendorMappingSupplierForEIPCompanyKey(supplierVendorMappings, currentCompanyKey)
    };
  }, {});
}
